import { z } from 'zod';
import {
  AckSchema,
  CurrencySchema,
  IdSchema,
  IdempotencyKeySchema,
  IsoDateTimeSchema,
  withMeta,
} from '../common';
import { SetNavSchema } from '../dataset/core';
import { defineProcedures, mutation, query, type RouterShape } from '../procedure';
import { defineFamily } from './family';

/** me.* — kernel router: the signed-in trader's profile, preferences, entitlements and the erasure entry point
 *  (SDD §7.4 Settings row: me.profile/update, me.preferences; §16.5 erasure; §17.3 entitlements). */
export const ProfileSchema = withMeta({
  userId: IdSchema,
  name: z.string(),
  handle: z.string(),
  email: z.email(),
  baseCurrency: CurrencySchema,
  timezone: z.string(),
  avatarUrl: z.string().nullable(),
  createdAt: IsoDateTimeSchema,
});
export type Profile = z.infer<typeof ProfileSchema>;

export const ProfileUpdateSchema = z
  .object({
    name: z.string().min(1).max(80).optional(),
    handle: z
      .string()
      .regex(/^[a-z0-9_]{3,24}$/)
      .optional(),
    baseCurrency: CurrencySchema.optional(),
    timezone: z.string().optional(),
  })
  .strict();

/** Preference definition rows ([key, label, description]) + the current boolean values — the Settings screen grammar. */
export const PreferenceDefSchema = z.tuple([z.string(), z.string(), z.string()]);
export const PreferencesSchema = withMeta({
  sections: z.array(SetNavSchema),
  defs: z.array(PreferenceDefSchema),
  values: z.record(z.string(), z.boolean()),
  theme: z.enum(['dark', 'light', 'system']),
});
export type Preferences = z.infer<typeof PreferencesSchema>;

/* ── Settings sections (SDD §7.4 Settings row; Baseline maps Settings → MOD-SETTINGS → API-ME → DB-IDENTITY) ──
 *
 *  The design package names nine sections in its Settings rail and ships markup for two, leaving the rest reading
 *  "This section is available in the full build". These are the contracts that full build needs. They live in `me.*`
 *  because that is where the Baseline already puts Settings, not because it was the nearest place to add them.
 */

/** One signed-in session, as the Security section lists them. */
export const SessionSummarySchema = z
  .object({
    sessionId: IdSchema,
    deviceId: IdSchema,
    deviceLabel: z.string(),
    platform: z.string(),
    /** Truncated to the network, never the full address: a settings screen does not need to identify a house. */
    approxLocation: z.string().nullable(),
    issuedAt: IsoDateTimeSchema,
    lastSeenAt: IsoDateTimeSchema,
    expiresAt: IsoDateTimeSchema,
    /** The session making the request. It is offered no revoke button of its own — that is what sign-out is. */
    current: z.boolean(),
  })
  .strict();
export type SessionSummary = z.infer<typeof SessionSummarySchema>;

export const SessionsSchema = withMeta({
  sessions: z.array(SessionSummarySchema),
  /** Devices seen but with no live session — a sign-in history, so a device the trader does not recognise is
   *  visible even after its session has lapsed. */
  dormantDevices: z.array(
    z
      .object({
        deviceId: IdSchema,
        deviceLabel: z.string(),
        platform: z.string(),
        lastSeenAt: IsoDateTimeSchema,
      })
      .strict(),
  ),
});

/** Security posture. Every field is a fact the server can prove, or an honest null. */
export const SecuritySchema = withMeta({
  email: z.email(),
  /** How the trader authenticates. `dev` is the sandbox login and says so. */
  authBinding: z.string(),
  /** Null when the binding does not expose it — an OIDC provider owns its own password policy. */
  passwordChangedAt: IsoDateTimeSchema.nullable(),
  /** Two-factor is the identity provider's, not ours (ADR: no second credential store). `null` = not reported. */
  twoFactor: z.enum(['on', 'off', 'provider-managed']).nullable(),
  /** The hash-chained audit log, verified on request. A log you cannot check is a log you are asked to trust. */
  audit: z
    .object({
      events: z.number().int(),
      verified: z.boolean(),
      /** The sequence where the chain broke, when it did. */
      brokenAt: z.number().int().nullable(),
      checkedAt: IsoDateTimeSchema,
    })
    .strict(),
});

/** The answer to a manual day lock (SDD §8.1 'Lock Day'). `resumed` means every account was already covered —
 *  a second press is the same act, not a second lock. */
export const LockDayResultSchema = withMeta({
  locked: z.literal(true),
  /** Accounts newly locked by THIS call (0 when resumed). */
  accountsLocked: z.number().int().nonnegative(),
  /** Accounts already under the day lock when this call arrived. */
  accountsAlreadyLocked: z.number().int().nonnegative(),
  expiresAt: IsoDateTimeSchema,
  resumed: z.boolean(),
});

/** An active lockout and the queue of risk changes waiting out their refractory delay (ADR-014, §11.2). */
export const LockoutStateSchema = withMeta({
  active: z.array(
    z
      .object({
        id: IdSchema,
        reason: z.string(),
        severity: z.enum(['soft', 'hard']),
        startedAt: IsoDateTimeSchema,
        expiresAt: IsoDateTimeSchema.nullable(),
        accountId: IdSchema.nullable(),
      })
      .strict(),
  ),
  /** Weakenings still serving their delay. Strengthenings never appear here — they apply at once. */
  pending: z.array(
    z
      .object({
        id: IdSchema,
        scope: z.enum(['profile', 'ruleSet', 'customRule', 'lockout']),
        settingKey: z.string(),
        currentValue: z.string().nullable(),
        requestedValue: z.string(),
        requestedAt: IsoDateTimeSchema,
        releasesAt: IsoDateTimeSchema,
      })
      .strict(),
  ),
  /** The operator's configured delay bounds, so the screen states the rule rather than restating a guess. */
  refractoryHours: z.object({ min: z.number(), max: z.number() }).strict(),
});

/** Typed settings — Display and Trading Defaults. Separate from `me.preferences`, whose values are booleans by
 *  contract and cannot carry a lot size or a timezone. */
export const SettingsSchema = withMeta({
  display: z
    .object({
      theme: z.enum(['dark', 'light', 'system']),
      density: z.enum(['comfortable', 'compact']),
      timezone: z.string(),
      /** How P&L is written. The trader's own money, in the form they read it in. */
      numberFormat: z.enum(['plain', 'accounting']),
      baseCurrency: CurrencySchema,
    })
    .strict(),
  trading: z
    .object({
      defaultSymbol: z.string(),
      defaultLots: z.number().positive(),
      /** Default stop distance as a fraction of price, so one setting travels across instruments. */
      defaultStopPct: z.number().positive(),
      defaultRewardRatio: z.number().positive(),
      /** Require the confirm step before an order leaves. Off is allowed; it is recorded when it changes. */
      confirmBeforeOrder: z.boolean(),
      /** Attach a stop to every entry. Weakening this is a risk change and queues (ADR-014). */
      requireStop: z.boolean(),
    })
    .strict(),
});
export type Settings = z.infer<typeof SettingsSchema>;

export const SettingsUpdateSchema = z
  .object({
    display: SettingsSchema.shape.display.partial().optional(),
    trading: SettingsSchema.shape.trading.partial().optional(),
  })
  .strict();

/** What every external system is bound to right now, and whether it can actually be reached.
 *
 *  This is the one screen where ground rule 2 is visible to the trader rather than only to a reader of the repo:
 *  a port bound to `sim` says simulated, and a live adapter with no credentials says dormant. */
export const IntegrationsSchema = withMeta({
  ports: z.array(
    z
      .object({
        key: z.string(),
        label: z.string(),
        binding: z.string(),
        /** True when this binding is a labelled simulation rather than the real system. */
        simulated: z.boolean(),
        /** False when the binding is real but cannot currently be reached — typically for want of credentials. */
        ready: z.boolean(),
        detail: z.string(),
      })
      .strict(),
  ),
});

/** A data export — everything the tenant owns, from the same lists erasure deletes from (§16.5). */
/** The data lifecycle the server actually implements, so the screen renders the policy rather than restating it.
 *  Deliberately has no "retainedForDays": nothing here is on a clock, and a field for one would invite a number. */
export const RetentionSchema = withMeta({
  classes: z.array(
    z
      .object({
        key: z.string(),
        label: z.string(),
        reason: z.string(),
        basis: z.enum(['account-lifetime', 'survives-erasure', 'destroyed-on-request']),
        onErasure: z.enum(['purge', 'anonymise', 'keep']),
        tables: z.array(z.string()),
      })
      .strict(),
  ),
});
export type Retention = z.infer<typeof RetentionSchema>;

export const DataExportSchema = withMeta({
  exportId: IdSchema,
  /** A link that expires. An export is the whole account in one file; a permanent URL for it is a liability. */
  url: z.string().nullable(),
  expiresAt: IsoDateTimeSchema.nullable(),
  bytes: z.number().int(),
  /** What went into it, by list, so the trader can see the export is complete rather than trust that it is. */
  contents: z.array(z.object({ list: z.string(), rows: z.number().int() }).strict()),
});
export type DataExport = z.infer<typeof DataExportSchema>;
export type SessionsList = z.infer<typeof SessionsSchema>;
export type SecurityPosture = z.infer<typeof SecuritySchema>;
export type LockoutState = z.infer<typeof LockoutStateSchema>;
export type Integrations = z.infer<typeof IntegrationsSchema>;

/** The plan tiers, owner-supplied (2026-08-22). `free` is the 7-day trial rather than a standing free plan. */
export const TierSchema = z.enum(['free', 'starter', 'trader', 'pro']);
export type Tier = z.infer<typeof TierSchema>;
export const EntitlementsSchema = withMeta({
  tier: TierSchema,
  /** Resolved feature keys the kernel cascade grants (SDD §17.3). */
  features: z.array(z.string()),
  limits: z
    .object({
      accountSlots: z.number().int(),
      aiDailyBudget: z.number().int(),
      voiceMinutes: z.number().int(),
      refreshRateSeconds: z.number().int(),
    })
    .strict(),
});
export type Entitlements = z.infer<typeof EntitlementsSchema>;

/** One pane of a saved layout: an instrument and a timeframe, and NOTHING of the market itself — layout state is
 *  independent from market-data state (directive §6). */
export const LayoutPaneSchema = z
  .object({ symbol: z.string().min(3).max(20), resolution: z.enum(['1', '5', '15', '60', '240', '1D']) })
  .strict();

export const LAYOUT_SCHEMA_VERSION = 1;

export const LayoutPresetSchema = withMeta({
  layoutId: IdSchema,
  name: z.string(),
  schemaVersion: z.number().int(),
  panes: z.array(LayoutPaneSchema),
  /** Whether the panes share one replay clock when synchronisation is enabled (SM-P6-06). */
  sync: z.boolean(),
  builtIn: z.boolean(),
});
export type LayoutPreset = z.infer<typeof LayoutPresetSchema>;

export const meProcedures = defineProcedures({
  'me.profile.get': query('Profile of the signed-in trader', z.void(), ProfileSchema),
  'me.profile.update': mutation(
    'Update profile fields (name, handle, base currency, timezone)',
    ProfileUpdateSchema,
    ProfileSchema,
  ),
  'me.profile.setAvatar': mutation(
    'Store a profile photo (small data-URL image → object store; profile.get serves a fresh signed link)',
    z
      .object({
        idempotencyKey: IdempotencyKeySchema,
        /** A data:image/png|jpeg|webp;base64 payload, capped small — this is an avatar, not a gallery. */
        image: z
          .string()
          .regex(/^data:image\/(png|jpeg|webp);base64,/)
          .max(400_000),
      })
      .strict(),
    ProfileSchema,
  ),
  'me.preferences.get': query(
    'Settings preference sections, definitions and values',
    z.void(),
    PreferencesSchema,
  ),
  'me.preferences.update': mutation(
    'Toggle preference values / theme',
    z
      .object({
        values: z.record(z.string(), z.boolean()).optional(),
        theme: z.enum(['dark', 'light', 'system']).optional(),
      })
      .strict(),
    PreferencesSchema,
  ),
  'me.entitlements.get': query(
    'Resolved tier, feature keys and per-tier limits',
    z.void(),
    EntitlementsSchema,
  ),
  'me.erasure.request': mutation(
    'Request account erasure (§16.5): journal audio + transcript purged together; 30-day hold',
    z.object({ idempotencyKey: IdempotencyKeySchema, confirmHandle: z.string() }).strict(),
    withMeta({ requestId: IdSchema, scheduledFor: IsoDateTimeSchema, ok: z.literal(true) }),
  ),
  'me.erasure.cancel': mutation(
    'Cancel a pending erasure request inside the hold window',
    z.object({ requestId: IdSchema }).strict(),
    AckSchema,
  ),

  // ── Settings sections ────────────────────────────────────────────────────────────────────────────────────────
  'me.sessions.list': query(
    'Active sessions and the devices they were opened from',
    z.void(),
    SessionsSchema,
  ),
  'me.sessions.revoke': mutation(
    'Revoke one session by id',
    z.object({ sessionId: IdSchema }).strict(),
    AckSchema,
  ),
  'me.sessions.revokeOthers': mutation(
    'Sign out every session but this one',
    z.object({ idempotencyKey: IdempotencyKeySchema }).strict(),
    withMeta({ revoked: z.number().int(), ok: z.literal(true) }),
  ),
  'me.security.get': query('Security posture, including audit-chain verification', z.void(), SecuritySchema),
  'me.lockouts.get': query(
    'Active lockouts and risk changes serving their refractory delay',
    z.void(),
    LockoutStateSchema,
  ),
  'me.lockouts.lockDay': mutation(
    "Manual day lock (SDD §8.1): a hard lockout on every linked account until the end of the UTC day. A voluntary STRENGTHENING, so it applies at once (ADR-014's delay is for weakenings); the calendar ends it — there is no unlock control, which is the point of pressing it.",
    z.object({ idempotencyKey: IdempotencyKeySchema }).strict(),
    LockDayResultSchema,
  ),
  'me.layouts.list': query(
    'Saved multi-screen layout presets (SM-P6-06) — versioned, user-owned, migrated forward on read',
    z.void(),
    withMeta({ layouts: z.array(LayoutPresetSchema), schemaVersion: z.number().int() }),
  ),
  'me.layouts.save': mutation(
    'Create or replace a named layout preset',
    z
      .object({
        idempotencyKey: IdempotencyKeySchema,
        name: z.string().min(1).max(64),
        panes: z.array(LayoutPaneSchema).min(1).max(6),
        sync: z.boolean(),
      })
      .strict(),
    LayoutPresetSchema,
  ),
  'me.layouts.delete': mutation(
    'Delete a layout preset',
    z.object({ idempotencyKey: IdempotencyKeySchema, layoutId: IdSchema }).strict(),
    withMeta({ deleted: z.boolean() }),
  ),
  'me.layouts.reset': mutation(
    'Remove every saved layout, returning the built-in default — recoverable-to-default is a contract, not a hope',
    z.object({ idempotencyKey: IdempotencyKeySchema }).strict(),
    withMeta({ layouts: z.array(LayoutPresetSchema) }),
  ),
  'me.dashboard.get': query(
    'Dashboard widget visibility (the Widgets screen edit mode) — the code-defined catalogue with per-user visibility',
    z.void(),
    withMeta({
      items: z.array(z.object({ key: z.string(), label: z.string(), visible: z.boolean() }).strict()),
    }),
  ),
  'me.dashboard.update': mutation(
    'Show or hide one dashboard widget',
    z.object({ idempotencyKey: IdempotencyKeySchema, key: z.string(), visible: z.boolean() }).strict(),
    withMeta({
      items: z.array(z.object({ key: z.string(), label: z.string(), visible: z.boolean() }).strict()),
    }),
  ),
  'me.settings.get': query('Display and trading defaults', z.void(), SettingsSchema),
  'me.settings.update': mutation('Update display or trading defaults', SettingsUpdateSchema, SettingsSchema),
  'me.integrations.get': query(
    'What each external port is bound to, and whether it is reachable',
    z.void(),
    IntegrationsSchema,
  ),
  'me.data.retention': query(
    'What is kept, for how long, and what an erasure does to it (VIEW-13)',
    z.void(),
    RetentionSchema,
  ),
  'me.data.export': mutation(
    'Export everything this tenant owns (§16.5), as a link that expires',
    z.object({ idempotencyKey: IdempotencyKeySchema }).strict(),
    DataExportSchema,
  ),
});
export type MeFamilyClient = RouterShape<typeof meProcedures>;

export const meFamily = defineFamily({
  name: 'me',
  title: 'Me (kernel prefs)',
  screens: ['Settings'],
  procedures: meProcedures,
  binding: 'simulated',
});
